import { useState } from "react";
import { Card, CardContent } from "../../ui/card";
import {
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { useChartColors } from "../../../hooks/useChartColors";
import { SummaryHeader } from "../../ui/SummaryHeader";
import AccountingLoader from "../../../components/loaders/AccountingLoader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../ui/tabs";
import { useAccountingWholesale } from "../hooks/useAccountingWholesale";
import { formatCurrency } from "../../../utils/formatCurrency";

export function WholesaleExpenseReport() {
  const colors = useChartColors("wholesale");
  const [selectedPeriod, setSelectedPeriod] = useState<'month' | 'quarter' | 'year' | 'all_time'>("month");

  const { data, isLoading } = useAccountingWholesale(selectedPeriod);
  const expenses = data?.total_expenses ?? [];
  const latest = expenses[expenses.length - 1];

  if (!colors) return null;

  const chartData = latest
    ? [
      { name: "COGS", value: latest.total_cogs },
      { name: "Returns", value: latest.total_returns },
      { name: "Shipping", value: latest.total_shipping },
      { name: "Discounts", value: latest.total_discounts },
      { name: "Tax", value: latest.total_tax },
      {
        name: "Shopify Syncio Marketing",
        value: latest.shopify_syncio_marketing_cogs,
      },
      {
        name: "Wholesale Promo Marketing",
        value: latest.wholesale_promo_marketing_cogs,
      },
    ].filter((d) => d.value && d.value !== 0)
    : [];

  const total = chartData.reduce((sum, d) => sum + d.value, 0);



  function formatPeriod(period: string, selected: string) {
    if (selected === "quarter" && period.includes("_")) {
      const [q, y] = period.split("_");
      return `${q} ${y}`;
    }
    if (selected === "all_time") {
      return "All Time";
    }
    return period;
  }

  return (
    <>
      <SummaryHeader title="Expense Report" />
      <Card>
        <CardContent>
          <Tabs defaultValue="month" onValueChange={(val) => setSelectedPeriod(val as any)} className="space-y-4">
            <TabsList className="flex flex-wrap gap-1">
              <TabsTrigger value="month">Monthly</TabsTrigger>
              <TabsTrigger value="quarter">Quarterly</TabsTrigger>
              <TabsTrigger value="year">Yearly</TabsTrigger>
              <TabsTrigger value="all_time">All Time</TabsTrigger>
            </TabsList>

            <TabsContent value={selectedPeriod} className="w-full h-full p-0 bg-transparent dark:bg-transparent rounded-none shadow-none-soft overflow-visible">
              {isLoading ? (
                <AccountingLoader variant="expense-rpt" itemCount={1} />
              ) : !chartData.length ? (
                <div className="p-4 text-center text-sm text-muted-foreground">
                  No data available
                </div>
              ) : (
                <>
                  <ResponsiveContainer width="100%" height={350}>
                    <PieChart>
                      <Pie
                        data={chartData}
                        cx="50%"
                        cy="50%"
                        outerRadius={110}
                        dataKey="value"
                        nameKey="name"
                        labelLine={false}
                      >
                        {chartData.map((_entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={colors[index % colors.length] || "#ccc"} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value) => [formatCurrency(value as number), "Amount"]}
                        contentStyle={{
                          backgroundColor: "var(--background)",
                          borderColor: "var(--border)",
                          borderRadius: "var(--radius)",
                          fontSize: "0.875rem",
                        }}
                        itemStyle={{ color: "var(--foreground)" }} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="flex justify-center gap-4 mt-4 px-2 flex-wrap">
                    {chartData.map((entry, index) => {
                      const percent = total ? (entry.value / total) * 100 : 0;
                      return (
                        <div key={entry.name} className="flex items-center gap-2 text-md text-foreground">
                          <span
                            className="w-3 h-3 rounded-sm inline-block"
                            style={{ backgroundColor: colors[index % colors.length] }}
                          />
                          <span>{entry.name}: {percent.toFixed(2)}%</span>
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-6 text-center">
                    <p className="text-lg font-semibold">
                      Total Expenses ({formatPeriod(latest.period, selectedPeriod)}): {formatCurrency(latest.total_expenses)}
                    </p>
                  </div></>

              )}
            </TabsContent>

          </Tabs>
        </CardContent>
      </Card>
    </>
  );
}
